import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "budget" }
const _hoisted_3 = {
  key: 0,
  class: "budget__row"
}
const _hoisted_4 = { class: "budget__title" }
const _hoisted_5 = { class: "budget__value" }
const _hoisted_6 = { class: "budget__row" }
const _hoisted_7 = { class: "budget__title" }
const _hoisted_8 = { class: "budget__value" }
const _hoisted_9 = {
  key: 1,
  class: "budget__row"
}
const _hoisted_10 = { class: "budget__title" }
const _hoisted_11 = { class: "budget__value" }
const _hoisted_12 = { class: "budget__row budget__row--total" }
const _hoisted_13 = { class: "budget__row--total__price" }

import {CompanyFeesService} from '@/brief-hub/brief/CompanyFeesService';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import type {ConditionsWhenConstraints} from '@/brief-hub/deposit/conditions/ConditionsWhenConstraints';
    import StrategyTooltipInfo from '@/brief-hub/strategy/deposit/conditions/StrategyTooltipInfo.vue';
    import type {BriefFreelancerProfile} from '@/brief-hub/store/modules/brief/types';
    import {useBriefStrategyStore} from '@/brief-hub/stores/briefStrategy.store';
    import {useConnectedUserStore} from '@/brief-hub/stores/connectedUser.store';
    import {WORKED_DAYS_MAPPING} from '@/common/config/variables';
    import {useGlobalProperties} from '@malt/vue3-use';
    import {addMonths, differenceInWeeks, addDays, addWeeks} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {computed, inject, PropType, ref, watch} from 'vue';
    import {context} from 'vue3-starter/src/typescript/app-context/AppContext';
    import {DurationUnit} from '@/common/api/api-raw-types';

    interface EstimatedBudget {
        freelancerQuote: number;
        fees: number;
        total: number;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'StrategyBudgetTooltip',
  props: {
        dailyFreelancerRate: {type: Number, required: true},
        durationEstimation: String,
        duration: Number,
        durationUnit: DurationUnit,
        startDate: Date,
        availability: {type: String as PropType<string | null>},
        budget: Number,
        constraints: {type: Object as PropType<ConditionsWhenConstraints>, required: true},
        briefFreelancerProfile: {type: Object as PropType<BriefFreelancerProfile>, required: true},
    },
  setup(__props) {

    const props = __props;

    const connectedUserStore = useConnectedUserStore();
    const {user: connectedUser} = storeToRefs(connectedUserStore);

    const briefStrategyStore = useBriefStrategyStore();
    const {brief} = storeToRefs(briefStrategyStore);
    const {$msg} = useGlobalProperties();

    const companyFeesService: CompanyFeesService | undefined = inject(DI.companyFeesService);

    const companyFees = ref<number>(0);

    const showDailyRate = computed<boolean>(() => props.durationEstimation === 'SUPERIOR_ONE_MONTH');

    const estimatedBudget = computed<EstimatedBudget>(() => {
        let freelancerQuote: number = 0;

        if (
            props.durationEstimation === 'SUPERIOR_ONE_MONTH' &&
            props.duration &&
            props.duration >= props.constraints.MIN_DURATION &&
            props.duration <= props.constraints.MAX_DURATION &&
            props.dailyFreelancerRate &&
            props.dailyFreelancerRate >= props.constraints.MIN_FREELANCER_RATE &&
            props.dailyFreelancerRate <= props.constraints.MAX_FREELANCER_RATE &&
            props.availability &&
            props.startDate
        ) {
            let endDate;
            switch (props.durationUnit) {
                case DurationUnit.DAYS:
                    endDate = addDays(props.startDate, props.duration);
                    break;
                case DurationUnit.WEEKS:
                    endDate = addWeeks(props.startDate, props.duration);
                    break;
                case DurationUnit.MONTHS:
                    endDate = addMonths(props.startDate, props.duration);
                    break;
            }
            const nbWeeks = differenceInWeeks(endDate, props.startDate);
            const workedDaysPerWeek: number = WORKED_DAYS_MAPPING[props.availability];

            freelancerQuote = props.dailyFreelancerRate * workedDaysPerWeek * nbWeeks;
        } else if (
            props.durationEstimation === 'INFERIOR_ONE_MONTH' &&
            props.budget &&
            props.budget >= props.constraints.MIN_BUDGET &&
            props.budget <= props.constraints.MAX_BUDGET
        ) {
            freelancerQuote = props.budget;
        }

        const fees = companyFees.value != null ? (companyFees.value / 100) * freelancerQuote : 0;

        return {
            freelancerQuote: freelancerQuote | 0,
            fees: fees | 0,
            total: (parseInt(`${freelancerQuote}`) + parseInt(`${fees}`)) | 0,
        };
    });

    const contractLabel = computed<string>(() => {
        return connectedUser.value && companyFees
            ? $msg('customer.brief.exec.form.conditions.when.tooltip.budget.contract')
            : $msg('customer.brief.exec.form.conditions.when.tooltip.budget.contract.unlogged');
    });

    const formattedCurrency = (value: number) => {
        return Intl.NumberFormat(context.locale, {
            style: 'currency',
            currency: brief.value!.briefDetails?.currencyCode,
        }).format(value);
    };

    watch(connectedUser, () => fetchCompanyFees());

    async function fetchCompanyFees() {
        if (connectedUser.value) {
            const [fees, error] = await companyFeesService!.fetchUserFees(connectedUser.value!);
            if (fees && !error) {
                companyFees.value = fees.strategyFees;
            }
        } else {
            const [fees, error] = await companyFeesService!.fetchDefaultFees();
            if (fees && !error) {
                companyFees.value = fees.strategyFees;
            }
        }
    }

    function init() {
        fetchCompanyFees();
    }

    init();

return (_ctx: any,_cache: any) => {
  const _component_VJoyWrapper = _resolveComponent("VJoyWrapper")!

  return (_openBlock(), _createBlock(StrategyTooltipInfo, {
    title: _unref($msg)('customer.brief.exec.form.conditions.when.tooltip.budget.title')
  }, {
    content: _withCtx(() => [
      _createVNode(_component_VJoyWrapper, {
        direction: "column",
        align: "stretch",
        "row-gap": "1",
        "column-gap": "1"
      }, {
        default: _withCtx(() => [
          _createElementVNode("p", { innerHTML: contractLabel.value }, null, 8, _hoisted_1),
          _createElementVNode("div", _hoisted_2, [
            (showDailyRate.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("p", _hoisted_4, _toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.tooltip.budget.daily.rate')), 1),
                  _createElementVNode("p", _hoisted_5, _toDisplayString(formattedCurrency(__props.dailyFreelancerRate)), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("p", _hoisted_7, _toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.tooltip.budget.quote')), 1),
              _createElementVNode("p", _hoisted_8, _toDisplayString(formattedCurrency(estimatedBudget.value.freelancerQuote)), 1)
            ]),
            (companyFees.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("p", _hoisted_10, _toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.tooltip.budget.fees')), 1),
                  _createElementVNode("p", _hoisted_11, _toDisplayString(formattedCurrency(estimatedBudget.value.fees)), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("p", null, _toDisplayString(_unref($msg)('customer.brief.exec.form.conditions.when.tooltip.budget.total')), 1),
              _createElementVNode("p", _hoisted_13, _toDisplayString(formattedCurrency(estimatedBudget.value.total)), 1)
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})